import React, { useMemo } from 'react';
import { useHits, useInstantSearch } from 'react-instantsearch';
import { useOptionalGetStandardByGuid } from '@components/ContentFilters/ContentFilterProvider';
import Box from '../Atoms/Box';
import Table from '../Atoms/Table/Table';
import { TableHeaderTypes } from '../Atoms/Table/Types';
import AlgoliaTableRow from './TableRow';
import type { AlgoliaLearnosityItem } from './types';
import AlgoliaTablePagination from './TablePagination';

const shimmerAnimation = {
  '@keyframes shimmer': {
    '0%': {
      backgroundPosition: '-1000px 0',
    },
    '100%': {
      backgroundPosition: '1000px 0',
    },
  },
};

const shimmerStyle = {
  animation: 'shimmer 2s infinite linear',
  background: 'linear-gradient(to right, #f6f7f8 4%, #edeef1 25%, #f6f7f8 36%)',
  backgroundSize: '1000px 100%',
  borderRadius: '4px',
};

interface AlgoliaTableRowsProps {
  tableColumns: TableHeaderTypes[];
  showCheckbox: boolean;
  selectedQuestions: string[];
  selectedTagColumns: string[];
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  pageSize: number;
  handleQuestionSelect: (questionId: string, selected: boolean) => void;
  handleTagsOpen: (hit: AlgoliaLearnosityItem) => void;
  emptyStateComponent?: React.ReactNode;
  selectButton?: (referenceId: string) => React.ReactNode;
}

const AlgoliaTableRows = ({
  tableColumns,
  showCheckbox,
  selectedQuestions,
  selectedTagColumns,
  currentPage,
  setCurrentPage,
  pageSize,
  handleQuestionSelect,
  handleTagsOpen,
  emptyStateComponent,
  selectButton = undefined,
}) => {
  const { results } = useHits<AlgoliaLearnosityItem>();
  const { status } = useInstantSearch();
  const isLoading = status === 'loading' || status === 'stalled';
  const getStandardByGuid = useOptionalGetStandardByGuid();

  const rows = React.useMemo(() => {
    if (!results?.hits) {
      return [];
    }
    return results.hits.map((hit, idx) =>
      AlgoliaTableRow({
        hit,
        showCheckbox,
        selectedQuestions,
        onQuestionSelect: handleQuestionSelect,
        selectedTagColumns,
        onTagsOpen: handleTagsOpen,
        currentPage,
        pageSize,
        rowIndex: idx,
        getStandardByGuid,
        selectButton,
      }),
    );
  }, [
    results?.hits,
    showCheckbox,
    selectedQuestions,
    selectedTagColumns,
    handleQuestionSelect,
    handleTagsOpen,
    currentPage,
    pageSize,
    getStandardByGuid,
    selectButton,
  ]);

  const totalPages = results?.nbPages || 0;
  const isEmpty = !isLoading && results?.hits?.length === 0;

  // Enhanced placeholder rows
  const placeholderRows = useMemo(
    () =>
      Array(pageSize)
        .fill(null)
        .map((_, index) => ({
          id: `placeholder-${index}`,
          reference: `placeholder-${index}`,
          content: (
            <Box
              sx={{
                width: '80%',
                height: '72px',
                ...shimmerStyle,
              }}
            />
          ),
          preview: (
            <Box
              sx={{
                width: '150px',
                height: '32px',
                ...shimmerStyle,
              }}
            />
          ),
          checkbox: showCheckbox ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                margin: '0 auto',
                padding: 0,
              }}
            >
              <Box
                sx={{
                  width: '28px',
                  height: '28px',
                  ...shimmerStyle,
                  borderRadius: '4px',
                }}
              />
            </Box>
          ) : null,
          item: (
            <Box sx={{ py: 1, height: '100px' }}>
              <Box
                sx={{
                  height: '16px',
                  width: '90%',
                  mb: 1,
                  ...shimmerStyle,
                }}
              />
              <Box
                sx={{
                  height: '16px',
                  width: '80%',
                  mb: 1,
                  ...shimmerStyle,
                }}
              />
              <Box
                sx={{
                  height: '16px',
                  width: '60%',
                  mb: 1,
                  ...shimmerStyle,
                }}
              />
              <Box
                sx={{
                  height: '14px',
                  width: '40%',
                  mt: 1,
                  ...shimmerStyle,
                }}
              />
            </Box>
          ),
          ...selectedTagColumns.reduce(
            (acc, columnKey) => ({
              ...acc,
              [columnKey.toLowerCase()]: (
                <Box
                  sx={{
                    width: '80%',
                    height: columnKey === 'Skill Display' ? '32px' : '16px',
                    ...shimmerStyle,
                  }}
                />
              ),
            }),
            {},
          ),
          tags: (
            <Box
              sx={{
                width: '100px',
                height: '32px',
                ...shimmerStyle,
              }}
            />
          ),
        })),
    [pageSize, showCheckbox, selectedTagColumns],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        position: 'relative',
        opacity: isLoading ? 0.85 : 1,
        transition: 'all 0.3s ease-in-out',
        '@keyframes shimmer': shimmerAnimation['@keyframes shimmer'],
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          width: '100%',
        }}
      >
        {isEmpty && emptyStateComponent ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '2rem',
              textAlign: 'center',
              height: '100%',
              minHeight: '200px',
              maxWidth: '555px',
              margin: 'auto',
              gap: '1rem',
            }}
          >
            <Box
              sx={{
                backgroundImage: 'url("/images/no-content.png")',
                aspectRatio: '16/9',
                width: '80%',
                margin: '0',
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
              }}
            />
            <Box
              sx={{
                marginBottom: '0.5rem',
                fontSize: '1.25rem',
                fontFamily: "'MontserratRegular'",
                fontWeight: 500,
                color: '#333',
              }}
            >
              No questions found. You can generate new questions with Co-Author.
            </Box>
            {emptyStateComponent}
          </Box>
        ) : (
          <Table
            columns={tableColumns}
            rows={isLoading ? placeholderRows : rows}
            hasPagination={false}
            sx={{
              '& td': {
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                transition: 'all 0.3s ease-in-out',
                p: isLoading ? '16px 8px' : undefined,
                textAlign: 'left',
                verticalAlign: 'middle',
                padding: '12px 8px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
              '& td[data-centered="true"]': {
                textAlign: 'center',
              },
              '& th': {
                textAlign: 'left',
                fontWeight: 'bold',
                padding: '12px 8px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                background: '#f5f5f5',
                position: 'sticky',
                top: 0,
                zIndex: 1,
              },
              '& th:first-of-type, & td:first-of-type': {
                textAlign: 'center',
              },
              '& tr:hover td': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
              tableLayout: 'fixed',
              width: '100%',
              minWidth: '100%',
            }}
          />
        )}
      </Box>

      {!isEmpty && totalPages > 1 && (
        <AlgoliaTablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}
    </Box>
  );
};

AlgoliaTableRows.displayName = 'AlgoliaTableRows';

export default AlgoliaTableRows;
export type { AlgoliaTableRowsProps, AlgoliaLearnosityItem };
